import { useCallback, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import api, {
  useGetIntersectionQuery,
  useUpdateIntersectionMutation,
  useResetIntersectionMutation,
  useRefreshIntersectionMutation,
  useSendDiagnosticCallMutation,
} from '../api';
import * as defaultIntersectionData from '../data/defaultDataIntersection.json';
import { setIntersection } from '../store/slice';
import openNotification from '../../../common/components/notification';

const useIntersection = ({ intersectionId }) => {
  const dispatch = useDispatch();

  const { data, isFetching, isError } = useGetIntersectionQuery({
    intersectionId,
  });

  useEffect(() => {
    if (isError) {
      openNotification({
        message: 'Error While Fetching Intersection Data',
        description: 'Unable to establish connection with Phase Selector, please try again later',
      });
      dispatch(setIntersection({}))
    }
  }, [isError, dispatch]);

  const [updateIntersection, updateIntersectionResponse] =
    useUpdateIntersectionMutation();
  const { isLoading: isUpdating } = updateIntersectionResponse;

  const [resetIntersection, resetIntersectionResponse] =
    useResetIntersectionMutation();
  const { isLoading: isResetting } = resetIntersectionResponse;

  const [sendDiagnosticCall, sendDiagnosticCallResponse] = useSendDiagnosticCallMutation();
  const { isLoading: isSendingDiagCall } = sendDiagnosticCallResponse;

  const [refresh] = useRefreshIntersectionMutation();

  const updateIntersectionCache = useCallback(
    (intersection) =>
      dispatch(
        api.util.updateQueryData(
          'getIntersection',
          { intersectionId: intersection.intersectionId },
          () => intersection
        )
      ),
    [dispatch]
  );

  const update = useCallback(
    (intersection) => {
      updateIntersection(intersection);
      updateIntersectionCache(intersection);
    },
    [updateIntersection, updateIntersectionCache]
  );

  const reset = useCallback(
    (intersection) => {
      const newIntersection = { ...intersection, ...defaultIntersectionData };
      resetIntersection(newIntersection);
      updateIntersectionCache(newIntersection);
    },
    [resetIntersection, updateIntersectionCache]
  );

  const isLoading = useMemo(
    () => isFetching || isUpdating || isResetting,
    [isFetching, isUpdating, isResetting]
  );

  const intersection = useMemo(() => {
    if (isLoading) return {};

    return {
      ...data,
      locationType: data?.locationType?.toUpperCase(),
    };
  }, [data, isLoading]);

  const sendDiagCall = useCallback(
    ({ channel, priority }) => {
      sendDiagnosticCall({
        intersectionId: intersection.intersectionId,
        channel,
        priority,
        turnSignal: 'Straight',
      });
    },
    [sendDiagnosticCall, intersection]
  );

  return {
    intersection,
    update,
    reset,
    refresh,
    sendDiagCall,
    updateIntersectionResponse,
    resetIntersectionResponse,
    isLoading,
    isSendingDiagCall,
  };
};

export default useIntersection;
